import { useState } from 'react';
import { formatMoney } from '../../shared/money';
import { useAuth } from '../../auth';
import type { JobListing } from '@modelcast/types';
import { jsonAuthHeaders } from '../../shared/authHeaders';
import { Modal } from '../../components/Modal';

interface ApplyModalProps {
  job: JobListing;
  onClose: () => void;
  onSubmit: () => void;
}

export function ApplyModal({ job, onClose, onSubmit }: ApplyModalProps) {
  const { getToken } = useAuth();
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { room, studio } = job;
  const jobText = room.modelAdText || room.modelExpectations;
  const isEvenSplit = room.settings?.revenueSplitMode === 'even';

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  };

  const formatCurrency = (cents: number | null) => {
    if (!cents) return null;
    return formatMoney(cents, room.currency).replace(/\.00$/, '');
  };

  const getPricingLabel = () => {
    if (room.pricingType === 'fixed' && room.ticketPrice) {
      return `${formatCurrency(room.ticketPrice)} per ticket`;
    }
    if (room.pricingType === 'donation') {
      return room.suggestedMinimum
        ? `Donation, ${formatCurrency(room.suggestedMinimum)} suggested`
        : 'Donation event';
    }
    return 'Free event';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch(`/api/jobs/rooms/${room.code}/apply`, {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({ message: message.trim() || undefined }),
      });

      if (response.ok) {
        onSubmit();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to submit application');
      }
    } catch (err) {
      console.error('Error submitting application:', err);
      setError('Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      backdropClassName="bg-black/60 backdrop-blur-sm"
      panelClassName="bg-bg rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      closeOnBackdrop
      closeOnEscape={false}
      ariaLabel="Apply to model"
    >
        {/* Header */}
        <div className="sticky top-0 bg-bg border-b border-line px-6 py-4 flex items-center justify-between">
          <div>
            <span className="text-[10.5px] uppercase text-text-faint">
              Apply to model
            </span>
            <h2 className="font-display text-xl font-light text-text">
              {room.name}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-text-dim hover:text-text text-xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5 space-y-5">
            {/* Session Details */}
            <div className="space-y-2">
              {studio && (
                <p className="text-sm text-text">
                  <span className="text-text-dim">Studio:</span> {studio.name}
                </p>
              )}
              {room.scheduledAt && (
                <p className="text-sm text-text">
                  <span className="text-text-dim">Date:</span> {formatDate(room.scheduledAt)}
                </p>
              )}
              <p className="text-sm text-text">
                <span className="text-text-dim">Tickets:</span> {getPricingLabel()}
              </p>
              {room.maxAttendees && (
                <p className="text-sm text-text">
                  <span className="text-text-dim">Capacity:</span> {room.maxAttendees} seats
                </p>
              )}
              {room.modelGuarantee && (
                <p className="text-sm text-text">
                  <span className="text-text-dim">Guarantee:</span> {formatCurrency(room.modelGuarantee)}
                </p>
              )}
              {isEvenSplit ? (
                <p className="text-sm text-text">
                  <span className="text-text-dim">Revenue split:</span> even split
                </p>
              ) : room.modelSplitPercent > 0 && (
                <p className="text-sm text-text">
                  <span className="text-text-dim">Revenue split:</span> {room.modelSplitPercent}% to model
                </p>
              )}
            </div>

            {/* Model Expectations */}
            {jobText && (
              <div className="p-4 bg-bg2 rounded-lg">
                <p className="text-[10.5px] uppercase text-text-faint mb-2">
                  What the host is looking for
                </p>
                <p className="text-sm text-text-dim leading-relaxed italic">
                  {jobText}
                </p>
              </div>
            )}

            {/* Message Input */}
            <div>
              <label className="block text-sm text-text mb-2">
                Short note to organizer <span className="text-text-faint">(optional)</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="A quick hello, your availability, or one relevant detail..."
                className="w-full h-28 px-4 py-3 border border-line rounded-lg text-sm text-text bg-white placeholder:text-text-faint focus:outline-none focus:border-text resize-none"
                maxLength={500}
              />
              <p className="mt-1 text-xs text-text-faint text-right">
                {message.length}/500
              </p>
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-bg border-t border-line px-6 py-4 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm text-text-dim hover:text-text transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2.5 bg-text text-bg text-sm font-medium rounded hover:bg-black transition-colors disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </form>
    </Modal>
  );
}
